/**
 * Visual tokens specific to the Vision Exceptions feature.
 *
 * These mirror the Figma "Hero Vision" export. Theme palette tokens are used
 * where possible (`theme.palette.*`, `theme.spacing(n)`); these are the
 * feature-specific colors and dimensions that don't live in the global theme.
 */

import { DetectionSource, DetectionStatus, DetectionType } from './types'

export const VISION_COLORS = {
  contentBg: '#f7f9fc',
  borderSoft: 'rgba(0,0,0,0.12)',
  borderHairline: 'rgba(0,0,0,0.06)',
  rowHoverBg: '#fafafa',
  rowExpandedBg: 'rgba(25,118,210,0.04)',
  primary: '#2962ff',
  primarySoftBg: 'rgba(41,98,255,0.04)',
  primarySoftHover: 'rgba(41,98,255,0.08)',
  textPrimary: 'rgba(0,0,0,0.87)',
  textSecondary: '#7a869a',
  textMuted: 'rgba(0,0,0,0.45)',
  linkBlue: '#29b6f6',
  // Source badge colors
  sourceMl: '#2962ff',
  sourceDriver: '#e65100',
  sourceManual: '#455a64',
  // Outcome colors
  confirmed: '#4caf50',
  rejected: '#ef5350',
  pending: '#cfd8dc',
  // Soft status/action backgrounds + text — centralized from inline hex
  // across the feature. Mirror the STATUS_CHIP_COLORS / Figma palette;
  // exact values preserved (no visual change).
  haulerAvatarBg: '#73e5a8',
  confirmedSoftBg: '#e8f5e9',
  confirmedSoftText: '#2e7d32',
  confirmedSoftHoverBg: '#c8e6c9',
  rejectedSoftBg: '#ffebee',
  rejectedSoftText: '#c62828',
  rejectedSoftHoverBg: '#ffcdd2',
  infoSoftBg: '#e3f2fd',
  infoSoftText: '#1976d2',
  amberSoftBg: '#fff3e0',
  mapBg: '#e8edf0',
  navSelectedBg: 'rgba(25,118,210,0.08)',
  navSelectedHoverBg: 'rgba(25,118,210,0.12)',
  hoverBgSoft: 'rgba(0,0,0,0.04)',
  filterToggleText: 'rgba(0,0,0,0.6)',
  chipAvatarBg: 'rgba(0,0,0,0.38)',
} as const

// Trimmed from 270 to give the listing toolbar room for one row of filters.
export const SIDEBAR_WIDTH = 220

export const HEADER_BAR_HEIGHT = 56

// Every control in the filter row sits on this, inputs and buttons alike.
export const FILTER_CONTROL_HEIGHT = 40

// 132 each lets search and five dropdowns share one row from ~1280px; the search takes the slack.
export const FILTER_DROPDOWN_SX = {
  minWidth: 132,
  flex: '0 0 auto',
  '& .MuiOutlinedInput-root': {
    bgcolor: 'common.white',
    typography: 'subtitle2',
    // subtitle2 carries the theme's secondary colour; the filter row reads as one set.
    color: VISION_COLORS.textPrimary,
    height: FILTER_CONTROL_HEIGHT,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: VISION_COLORS.borderSoft },
}

export const FILTER_SEARCH_SX = {
  flex: { xs: '1 1 100%', md: '1 1 240px' },
  minWidth: { md: 200 },
  '& .MuiOutlinedInput-root': { height: FILTER_CONTROL_HEIGHT },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: VISION_COLORS.borderSoft },
}

export const FILTER_SEARCH_INPUT_SX = { typography: 'subtitle2', color: VISION_COLORS.textPrimary, bgcolor: 'common.white' }

export const FILTER_RESET_SX = {
  textTransform: 'none',
  typography: 'subtitle2',
  color: VISION_COLORS.textPrimary,
  flex: '0 0 auto',
  height: FILTER_CONTROL_HEIGHT,
}

export const SOURCE_BADGE_COLORS: Record<DetectionSource, { bg: string; color: string }> = {
  ml: { bg: '#e3f2fd', color: VISION_COLORS.sourceMl },
  driver: { bg: '#fff3e0', color: VISION_COLORS.sourceDriver },
  manual: { bg: '#eceff1', color: VISION_COLORS.sourceManual },
}

export const STATUS_CHIP_COLORS: Record<DetectionStatus, { bg: string; color: string }> = {
  pending: { bg: '#fff8e1', color: '#e65100' },
  confirmed: { bg: '#e8f5e9', color: '#2e7d32' },
  rejected: { bg: '#ffebee', color: '#c62828' },
  // VIS-4: same blue family as `info` in the common StatusChip so the
  // flagged badge reads as "set aside for review" — distinct from the
  // green confirmed / red rejected / yellow pending tones.
  flagged: { bg: '#e3f2fd', color: '#1976d2' },
  // VIS-7: teal/cyan for auto-confirmed (BE-side auto-approval) — visually
  // adjacent to confirmed but distinct so the reviewer knows no human
  // touched it. `modified` reads as a warm amber — adjacent to pending
  // but distinct: the row was approved but with edits.
  'auto-confirmed': { bg: '#e0f7fa', color: '#00838f' },
  modified: { bg: '#fff3e0', color: '#bf360c' },
}

const DEFAULT_DETECTION_CHIP = { bg: '#eceff1', color: 'rgba(0,0,0,0.7)' }

export const DETECTION_TYPE_CHIP_COLORS: Partial<Record<DetectionType, { bg: string; color: string }>> = {
  safety: { bg: '#d1c4e9', color: 'rgba(0,0,0,0.87)' },
  'no-access': { bg: '#e1bee7', color: 'rgba(0,0,0,0.87)' },
  'not-out': { bg: '#fce4ec', color: 'rgba(0,0,0,0.87)' },
  damage: { bg: '#ffe0b2', color: 'rgba(0,0,0,0.87)' },
  overload: { bg: '#ffecb3', color: 'rgba(0,0,0,0.87)' },
  contamination: { bg: '#c5cae9', color: 'rgba(0,0,0,0.87)' },
  extra: { bg: '#fff9c4', color: 'rgba(0,0,0,0.87)' },
  special: { bg: '#9e9e9e', color: '#fff' },
  weight: { bg: '#bbdefb', color: 'rgba(0,0,0,0.87)' },
  location: { bg: '#e8f5e9', color: 'rgba(0,0,0,0.87)' },
  color: { bg: '#f3e5f5', color: 'rgba(0,0,0,0.87)' },
  size: { bg: '#e3f2fd', color: 'rgba(0,0,0,0.87)' },
  'serial-number': { bg: '#e0f7fa', color: 'rgba(0,0,0,0.87)' },
}

export const getDetectionTypeChipColor = (type: DetectionType): { bg: string; color: string } =>
  DETECTION_TYPE_CHIP_COLORS[type] ?? DEFAULT_DETECTION_CHIP
